import React from "react";
import "./trajectory-loader.scss";

export interface TrajectoryLoaderProps {
  /** Width of the loader container */
  size?: string;
  /** Animation speed (e.g., "2s", "1.5s") */
  speed?: string;
  /** Progress percentage (0-100). When 0, shows endless streaming animation */
  progress?: number;
  /** Additional CSS class name */
  className?: string;
}

/**
 * TrajectoryLoader - A financial chart-style loader with gradient streaming animation
 * 
 * Features:
 * - Endless streaming mode when progress = 0
 * - Progress mode when progress > 0
 * - Gradient colors representing financial growth
 * - Grid background for chart-like appearance
 */
export const TrajectoryLoader: React.FC<TrajectoryLoaderProps> = ({ 
  size = "200px", 
  speed = "2s",
  progress = 0,
  className = ""
}) => {
  // Contained path within viewBox for better control
  const streamLength = 80; // Length of visible streak
  const totalPathLength = 300; // Total path length for smooth animation
  
  // For progress control, we show partial completion
  const progressOffset = progress > 0 ? 
    totalPathLength - (streamLength * progress / 100) : 
    totalPathLength;

  return (
    <div 
      className={`trajectory-loader ${progress === 0 ? 'endless-stream' : 'progress-mode'} ${className}`}
      style={{ 
        width: size, 
        height: "120px",
        animationDuration: speed 
      }}
    >
      <svg viewBox="0 0 200 120" className="trajectory-svg">
        <defs>
          <linearGradient id="trajectoryStreamGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4f46e5" />
            <stop offset="33%" stopColor="#3b82f6" />
            <stop offset="66%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        
        {/* Grid lines */}
        <g className="grid-lines">
          {[20, 40, 60, 80, 100].map(y => (
            <line key={y} x1="0" y1={y} x2="200" y2={y} stroke="rgba(148, 163, 184, 0.3)" strokeWidth="0.5" />
          ))}
          {[40, 80, 120, 160].map(x => (
            <line key={x} x1={x} y1="0" x2={x} y2="120" stroke="rgba(148, 163, 184, 0.3)" strokeWidth="0.5" />
          ))}
        </g>
        
        {/* Trajectory path */}
        <path
          className="trajectory-stream-path"
          d="M20,85 L50,65 L80,90 L110,70 L140,40 L170,65 L200,35"
          fill="none"
          stroke="url(#trajectoryStreamGradient)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ 
            strokeDasharray: progress > 0 ? 'none' : `${streamLength} ${totalPathLength - streamLength}`,
            strokeDashoffset: progress > 0 ? progressOffset : 0,
            transition: progress > 0 ? 'stroke-dashoffset 0.3s ease' : 'none'
          }}
        />
      </svg>
    </div>
  );
};

export default TrajectoryLoader;
